import { Button, Dialog, IconButton, Switch } from '@mui/material';
import { FilterKeys, GetReportRecipientsQueryFilters, ReportForAssessment } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  FilterRow,
  GroupSelectMultiple,
  PropertyTypeSelectMultiple,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../hooks/useSchool';

type ReportPreviewModalFiltersPopupProps = {
  report: ReportForAssessment;
  filters: GetReportRecipientsQueryFilters;
  defaultFilters: GetReportRecipientsQueryFilters;
  onClose: () => void;
  onSetFilters: (v: GetReportRecipientsQueryFilters) => void;
};

export const ReportPreviewModalFiltersPopup: FC<ReportPreviewModalFiltersPopupProps> = ({
  onClose,
  onSetFilters,
  report,
  filters: actualFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();

  const { schoolId } = useSchool();

  const [filters, setFilters] = useState<GetReportRecipientsQueryFilters>(actualFilters);

  const handleApply = useCallback(() => {
    onSetFilters(filters);
    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const {
    onClearAgeGroup,
    onSelectAgeGroup,
    onSelectGroup,
    onClearGroup,
    onClearOnlyTutorGroups,
    onSelectOnlyTutorGroups,
    onSelectSubject,
    onClearSubject,
    onSelectHouse,
    onClearHouse,
  } = useMemo(() => {
    const onSelectAgeGroup = (v: string[]) => {
      setFilters((filters) => ({
        ...filters,
        [FilterKeys.AgeGroup]: toggleMultipleValueArrayProperty(filters[FilterKeys.AgeGroup], v),
      }));
    };

    const updateFilter = (key: keyof typeof actualFilters) => (id: string) => {
      setFilters((filters) => ({
        ...filters,
        [key]: filters[key]?.includes(id)
          ? filters[key]?.filter((ct) => ct !== id)
          : [...(filters[key] || []), id],
      }));
    };

    const clearFilter = (key: keyof typeof actualFilters) => () => {
      setFilters((filters) => ({
        ...filters,
        [key]: [],
      }));
    };

    return {
      onClearOnlyTutorGroups: () =>
        setFilters((filters) => ({
          ...filters,
          [FilterKeys.OnlyTutorGroups]: undefined,
        })),
      onSelectOnlyTutorGroups: () =>
        setFilters((filters) => ({
          ...filters,
          [FilterKeys.OnlyTutorGroups]: ['1'],
          [FilterKeys.Subject]: undefined,
        })),
      onSelectSubject: (id: string) => {
        setFilters((filters) => ({
          ...filters,
          [FilterKeys.OnlyTutorGroups]: undefined,
          [FilterKeys.Subject]: filters[FilterKeys.Subject]?.includes(id)
            ? filters[FilterKeys.Subject]?.filter((ct) => ct !== id)
            : [...(filters[FilterKeys.Subject] || []), id],
        }));
      },
      onSelectHouse: updateFilter(FilterKeys.House),
      onClearHouse: clearFilter(FilterKeys.House),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onSelectGroup: updateFilter(FilterKeys.Group),
      onClearGroup: clearFilter(FilterKeys.Group),
      onSelectAgeGroup,
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
    };
  }, []);

  const groupFilters = useMemo(() => {
    return {
      [FilterKeys.Assessment]: report.assessments.map((assessment) => assessment.id),
    };
  }, [report.assessments]);

  if (!schoolId) return null;
  const isOnlyTutorGroups = !!filters.only_tutor_groups?.length;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'reports-Filter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
          <AgeGroupSelectMultiple
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={onClearAgeGroup}
            optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
            userRole={SchoolUserRole.Student}
            selectedIds={filters.age_group || []}
            onSelect={onSelectAgeGroup}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'schoolProperty-House' })}>
          <PropertyTypeSelectMultiple
            borderOnHover
            schoolId={schoolId}
            userRole={SchoolUserRole.Student}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            selectedIds={filters.house || []}
            onSelectId={onSelectHouse}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'section-Subjects' })}>
          <SubjectSelectMultiple
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={onClearSubject}
            selectedIds={filters.subject || []}
            onSelectSubjectId={onSelectSubject}
            placeholder={
              isOnlyTutorGroups
                ? formatMessage({ id: 'groups-GroupSubject-disabledFilter-Hint' })
                : undefined
            }
            disabled={isOnlyTutorGroups}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'groups-OnlyTutorGroups' })}>
          <Switch
            sx={{ ml: 1.25 }}
            onChange={(_, checked) => {
              checked ? onSelectOnlyTutorGroups() : onClearOnlyTutorGroups();
            }}
            checked={isOnlyTutorGroups}
          />
        </FilterRow>
        <FilterRow label={formatMessage({ id: 'groups-Group' })}>
          <GroupSelectMultiple
            borderOnHover
            onClear={onClearGroup}
            schoolId={schoolId}
            selectedIds={filters.group ?? []}
            onSelectGroupId={onSelectGroup}
            filters={groupFilters}
          />
        </FilterRow>
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};
